import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

// Install a global axios interceptor so every API call carries the token.
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// On 401 from the API, drop credentials so the LoginGate is shown.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      // Soft-reload only if not already on a login screen flow.
      if (!window.__auctionai_logging_out) {
        window.__auctionai_logging_out = true;
        // Allow current call stack to unwind before reloading.
        setTimeout(() => window.location.reload(), 0);
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/login', { username, password });
      const { token: t, user: u } = res.data;
      localStorage.setItem(TOKEN_KEY, t);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setToken(t);
      setUser(u);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.error === 'invalid_credentials'
        ? 'Invalid username or password'
        : 'Login failed';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    // Best-effort server notification (stateless, safe to ignore failure).
    axios.post('/api/auth/logout').catch(() => {});
  }, []);

  // Validate token on first mount, in case the server-side secret changed
  // or the user was deleted/disabled.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get('/api/auth/me')
      .then((r) => { if (!cancelled) setUser(r.data); })
      .catch(() => { if (!cancelled) logout(); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const value = useMemo(() => ({
    token,
    user,
    role: user?.role || null,
    isAuthenticated: !!token && !!user,
    loading,
    error,
    login,
    logout,
  }), [token, user, loading, error, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const hasRole = (role, ...allowed) => {
  if (!role) return false;
  if (role === 'superuser') return true;
  return allowed.includes(role);
};
